import { Field, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import images from '../../assets/images';
import FederatedLayout from '../../layout/federatedLayout';
import {
  deleteFederatedAccount,
  migrateAccountInHouse,
  verifySecureLink,
} from '../../services/federated-account-migration';
import { AppDispatch } from '../../store';
import { changeLanguage } from '../../store/actions/app.Actions';
import { companyNameReplace } from '../../utils';
import { AccountMigrationEmailSchema } from '../../validation/login/login-validation';
import AccessDenied from './components/AccessDenied';
import SocialMediaLoginForm from '../login/SocialMediaLoginForm';
import { Common } from '../../utils/constants';
import ReactstrapFormikFloatingLabelInput from '../../components/reactstrap-formik/ReactstrapFormikFloatingLabelInput';
import { ICommonErrorResponse } from 'models/common-model';

const SESSION_KEY = (token: string) => `${Common.FederatedAccountMigrationSessionKeys.MIGRATION_OTP_VERIFIED}:${token}`;

const ManageAccess: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { token: rawToken } = useParams<{ token?: string }>();
  const token = (rawToken || '').trim();
  const { search } = useLocation();
  const query = useMemo(() => new URLSearchParams(search), [search]);

  const [apiError, setApiError] = useState<string>('');
  const [expanded, setExpanded] = useState<'email' | 'social' | 'delete' | null>(null);
  const [emailBusy, setEmailBusy] = useState<boolean>(false);
  const [delBusy, setDelBusy] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>('');

  useEffect(() => {
    const lang = query.get('lang');
    if (lang) {
      dispatch(changeLanguage({ code: lang }));
    }
  }, [query, dispatch]);

  useEffect(() => {
    const gateRaw = sessionStorage.getItem(SESSION_KEY(token));
    try {
      const gate = gateRaw ? JSON.parse(gateRaw) : null;
      if (!(gate && gate.ok && gate.exp && gate.exp > Date.now())) {
        navigate(`/federated-accounts/migration/verify-otp/${token}`);
        return;
      }
    } catch (_e) {
      navigate(`/federated-accounts/migration/verify-otp/${token}`);
      return;
    }

    (async () => {
      try {
        const response = await verifySecureLink(token);
        if (!response?.Data) {
          setApiError(t('federatedAccountMigration.message.accessDenied') as string);
        } else {
          if (response?.Data?.first_name) setDisplayName(response.Data.first_name);
        }
      } catch (error) {
        setApiError((error as ICommonErrorResponse)?.response?.data?.message || String(error));
      }
    })();
  }, [navigate, token]);

  const handleEmailSubmit = async (values: { email: string }) => {
    setEmailBusy(true);
    try {
      const res = await migrateAccountInHouse(token, values.email.trim());
      const msg = res?.Messages;
      sessionStorage.setItem(Common.FederatedAccountMigrationSessionKeys.MIGRATION_SUCCESS, msg);
      sessionStorage.removeItem(Common.FederatedAccountMigrationSessionKeys.MIGRATION_TOKEN);
      sessionStorage.removeItem(SESSION_KEY(token));
      navigate('/federated-accounts/migration/success', {
        state: { message: msg },
        replace: true,
      });
    } catch (error) {
      toast.error((error as ICommonErrorResponse)?.response?.data?.message || String(error));
    } finally {
      setEmailBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDelBusy(true);
    try {
      const res = await deleteFederatedAccount(token);
      const msg = res?.Messages;
      sessionStorage.setItem(Common.FederatedAccountMigrationSessionKeys.MIGRATION_SUCCESS, msg);
      sessionStorage.removeItem(Common.FederatedAccountMigrationSessionKeys.MIGRATION_TOKEN);
      sessionStorage.removeItem(SESSION_KEY(token));
      navigate('/federated-accounts/migration/success', {
        state: { message: msg },
        replace: true,
      });
    } catch (error) {
      toast.error((error as ICommonErrorResponse)?.response?.data?.message || String(error));
    } finally {
      setDelBusy(false);
    }
  };
  if (apiError) {
    return (
      <>
        <Helmet>
          <title>{companyNameReplace(t('federatedAccountMigration.title'))} </title>
        </Helmet>
        <div className="login-page container-fluid p-0">
          <div className="row h-100 no-gutters">
            <div className="col-md login-background-block">
              <a href="#" title={companyNameReplace(t<string>('login.label.logo'))} className="instavolt-logo">
                <img src={images.logoPNG} alt={companyNameReplace(t<string>('login.label.logo'))} />
              </a>
            </div>
            <div className="col-md-auto login-section">
              <div className="login-outer">
                <AccessDenied message={apiError} />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <FederatedLayout displayName={displayName}>
      <div className="p-4">
        <div className="title-outer">
          <h1 className="manage-access-title">{t('federatedAccountMigration.header')}</h1>
        </div>
        <p className="manage-access-sub-title">{t('federatedAccountMigration.subtitle')}</p>

        <div className={`migrate-option-card form-group ${expanded === 'email' ? 'active' : ''}`}>
          <div
            className="custom-radio d-flex align-items-center migrate-option-card-label"
            onClick={() => {
              setExpanded(expanded === 'email' ? null : 'email');
            }}
          >
            <input type="radio" className="hidden-radio" checked={expanded === 'email'} readOnly />
            <span className="custom-radio-dot"></span>
            <span className="ml-2 noselect migrate-option-label-text">
              {t('federatedAccountMigration.option.email')}
            </span>
          </div>
          {expanded === 'email' && (
            <div className="card mt-3 p-3 shadow-none">
              <Formik
                initialValues={{ email: '' }}
                validationSchema={AccountMigrationEmailSchema(t)}
                onSubmit={handleEmailSubmit}
              >
                {({ handleSubmit }) => (
                  <form onSubmit={handleSubmit} noValidate className="form-primary">
                    <Field
                      type="email"
                      name="email"
                      id="email"
                      label={t<string>('federatedAccountMigration.placeholder.email')}
                      className="form-group noselect migrate-email-input"
                      autoComplete="off"
                      component={ReactstrapFormikFloatingLabelInput}
                    />
                    <button type="submit" className="btn btn-primary mt-3 migrate-email-button" disabled={emailBusy}>
                      {t('federatedAccountMigration.button.continue')}
                    </button>
                  </form>
                )}
              </Formik>
            </div>
          )}
        </div>

        <div className={`migrate-option-card form-group ${expanded === 'social' ? 'active' : ''}`}>
          <div
            className="custom-radio d-flex align-items-center migrate-option-card-label"
            onClick={() => setExpanded(expanded === 'social' ? null : 'social')}
          >
            <input type="radio" className="hidden-radio" checked={expanded === 'social'} readOnly />
            <span className="custom-radio-dot"></span>
            <span className="ml-2 noselect migrate-option-label-text">
              {t('federatedAccountMigration.option.social') || 'Sign in quickly with a social account'}
            </span>
          </div>
          {expanded === 'social' && (
            <div className="card mt-2 p-3 shadow-none">
              <div className="login-page manage-access-social-media d-flex justify-content-center">
                <div className="login-contain manage-access-social-media-container">
                  <SocialMediaLoginForm migrationToken={token} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`migrate-option-card form-group ${expanded === 'delete' ? 'active' : ''}`}>
          <div
            className="custom-radio d-flex align-items-center migrate-option-card-label"
            onClick={() => setExpanded(expanded === 'delete' ? null : 'delete')}
          >
            <input type="radio" className="hidden-radio" checked={expanded === 'delete'} readOnly />
            <span className="custom-radio-dot"></span>
            <span className="ml-2 noselect migrate-option-label-text">
              {t('federatedAccountMigration.option.delete')}
            </span>
          </div>
          {expanded === 'delete' && (
            <div className="card mt-3 p-3 shadow-none">
              <p className="manage-access-delete-text">{t('federatedAccountMigration.message.deleteWarning')}</p>
              <button
                className="btn btn-primary migrate-email-delete-button"
                onClick={handleDeleteAccount}
                disabled={delBusy}
              >
                {t('federatedAccountMigration.button.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
    </FederatedLayout>
  );
};

export default ManageAccess;
